import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router';
import {
  RussiaRequestChatReadUpdatedEventSchema,
  RussiaRequestChatUpdatedEventSchema,
  RussiaRequestsUpdatedEventSchema,
} from '@magimp/shared-realtime';
import { fetchRussiaRequest } from '../api/russia-requests/index.js';
import { evictRowFromListCaches } from '../features/requests/queries.js';
import {
  russiaRequestsKeys,
  narrowInvalidateForRussiaRequest,
  invalidateRussiaUnreadSummariesContaining,
} from '../features/russia-requests/queries.js';
import { ApiClientError } from '../api/errors.js';
import type { ToastInput } from '../stores/toast.js';

/**
 * @aiPurpose builds the socket-level handlers for the Russia (RF) request
 *   domain (`russia-requests:updated`, `russia-request-chat:updated`,
 *   `russia-request-chat-read:updated`). Splits out of `RealtimeBootstrap`
 *   so the cohesive RF cache-coherence + visibility-probe logic lives next
 *   to its tests and the bootstrap effect remains a thin socket-lifecycle
 *   shell.
 * @aiSecurity #706 Slice B: the wire payload is the strict `{ id, reason }`
 *   shape — no `endAddress` PII leaks here. Server-side row-level visibility
 *   is re-applied on the triggered REST refetch, so a user without
 *   `russiaRequests.read` simply re-receives a 404 and the cache stays empty.
 */
export interface RussiaRealtimeDeps {
  queryClient: QueryClient;
  pathnameRef: MutableRefObject<string>;
  navigateRef: MutableRefObject<NavigateFunction>;
  showToastRef: MutableRefObject<(t: ToastInput) => string>;
  tRef: MutableRefObject<TFunction<'requests'>>;
}

export function createRussiaRealtimeHandlers(deps: RussiaRealtimeDeps) {
  const { queryClient, pathnameRef, navigateRef, showToastRef, tRef } = deps;

  // INVARIANT (#1159 PR5): symmetric Russia handling — the seeded
  //   logist_ru classifies to scope='all' and still receives the
  //   broadcast. On a non-deleted update we probe the detail; if it
  //   404s, the open drawer (mounted at `/russia-rates/:id`) is
  //   replaced with the list URL and a neutral toast surfaces.
  const handleRussiaRowVanished = (id: string): void => {
    const path = pathnameRef.current;
    const drawerPath = `/russia-rates/${encodeURIComponent(id)}`;
    if (path === drawerPath) {
      navigateRef.current('/russia-rates', { replace: true });
    }
    // INVARIANT (#1159 AC#9): the per-id Russia caches are keyed at
    //   `['russia-request', id, ...]` (detail, audit-entries, carriers)
    //   and `['russia-request-chat', id]`. A prefix-removeQueries on
    //   the detail key catches every per-id sub-resource in one call;
    //   chat lives under its own prefix so it needs an explicit pass.
    //   The list caches are rewritten to drop the row immediately.
    queryClient.removeQueries({ queryKey: russiaRequestsKeys.detail(id) });
    queryClient.removeQueries({ queryKey: russiaRequestsKeys.chat(id), exact: true });
    evictRowFromListCaches(queryClient, russiaRequestsKeys.all, id);
  };
  const probeRussiaVisibility = (id: string): void => {
    void queryClient
      .fetchQuery({
        queryKey: russiaRequestsKeys.detail(id),
        queryFn: ({ signal }) => fetchRussiaRequest(id, { signal }),
        staleTime: 0,
      })
      .catch((err) => {
        if (err instanceof ApiClientError && err.httpStatus === 404) {
          handleRussiaRowVanished(id);
          showToastRef.current({ tone: 'info', text: tRef.current('rowGone') });
        }
      });
  };
  const russiaRequestsUpdatedHandler = (raw: unknown) => {
    const parsed = RussiaRequestsUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    const id = parsed.data.id;
    if (parsed.data.reason === 'deleted') {
      handleRussiaRowVanished(id);
      return;
    }
    // #1664: row-scoped narrowing mirroring China (#1593) — detail + only
    //   the list queries that currently CONTAIN the row, replacing the broad
    //   `russiaRequestsKeys.all` invalidation. ACCEPTED BEHAVIOR CHANGE: a
    //   `created` row no longer forces refetch of lists that don't contain
    //   it; discovery happens via lifecycle catch-up on focus/online.
    narrowInvalidateForRussiaRequest(queryClient, id);
    // WHY (#1159 PR5, mirror of China): only non-created reasons can lose
    //   visibility — a fresh `created` row never 404s right after creation,
    //   so skip the probe there.
    if (parsed.data.reason === 'created') return;
    probeRussiaVisibility(id);
  };
  // #1067: RF chat realtime invalidation. Mirrors the China handlers
  //   but routes through `russiaRequestsKeys.chat*` so RF and China
  //   caches stay isolated. Backend chat-party gate is authoritative
  //   on the triggered REST refetch — a non-party simply receives 404
  //   and the cache stays empty.
  const russiaRequestChatUpdatedHandler = (raw: unknown) => {
    const parsed = RussiaRequestChatUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    void queryClient.invalidateQueries({
      queryKey: russiaRequestsKeys.chat(parsed.data.russiaRequestId),
    });
    // #1664: id-scoped — only summaries holding this russiaRequestId refetch.
    invalidateRussiaUnreadSummariesContaining(queryClient, parsed.data.russiaRequestId);
  };
  const russiaRequestChatReadUpdatedHandler = (raw: unknown) => {
    const parsed = RussiaRequestChatReadUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    // INVARIANT (#1067 mirror of #570): `read` may flip
    //   `readByCounterparty` so the chat slice must refresh; `mark` /
    //   `unmark` are personal-state flips touching only the summary.
    if (parsed.data.kind === 'read') {
      void queryClient.invalidateQueries({
        queryKey: russiaRequestsKeys.chat(parsed.data.russiaRequestId),
      });
    }
    // #1664: id-scoped — only summaries holding this russiaRequestId refetch.
    invalidateRussiaUnreadSummariesContaining(queryClient, parsed.data.russiaRequestId);
  };

  return {
    russiaRequestsUpdatedHandler,
    russiaRequestChatUpdatedHandler,
    russiaRequestChatReadUpdatedHandler,
  };
}
